import SEO from "@/components/SEO";
import { FadeUp, MaskReveal, Stagger, StaggerItem } from "@/components/Reveal";
import Raizes from "@/sections/Raizes";
import CtaBand from "@/sections/CtaBand";
import { PROFESSIONAL } from "@/data/site";

const TRAJETORIA = [
  { year: "2021", text: "Início da trajetória prática na área clínica, ainda durante a graduação, por meio dos estágios em Psicologia." },
  { year: "2023", text: "Conclusão da formação em Psicologia e continuidade da prática clínica." },
  { year: "Hoje", text: "Prática clínica e aprofundamento profissional na área da Neuropsicologia, com crianças, adolescentes e adultos." },
];

const PILARES = [
  {
    n: "01",
    title: "Propósito",
    text: "Contribuir para a compreensão das experiências, dificuldades e potencialidades de cada pessoa por meio do cuidado psicológico e neuropsicológico.",
  },
  {
    n: "02",
    title: "Missão",
    text: "Oferecer serviços de Psicologia e Neuropsicologia pautados em ética, responsabilidade, acolhimento, individualidade e conhecimento científico.",
  },
  {
    n: "03",
    title: "Visão",
    text: "Construir o espaço Raízes — um projeto em desenvolvimento, guiado pela qualidade técnica, pelo cuidado humano e pelo compromisso ético.",
  },
];

const VALORES = ["Ética", "Ciência", "Acolhimento", "Escuta", "Respeito", "Individualidade", "Responsabilidade", "Desenvolvimento contínuo"];

const Sobre = () => (
  <>
    <SEO
      title="Sobre — Sabrina Mota, Psicóloga e Neuropsicóloga CRP 06/201923"
      description="Conheça a trajetória de Sabrina Kettyllin Mariano Gaia Mota, Psicóloga CRP 06/201923, e a identidade Raízes, símbolo do seu trabalho."
      path="/sobre"
    />
    <section className="pt-28 lg:pt-40" data-testid="sobre-page">
      <div className="container-x grid items-start gap-14 pb-20 lg:grid-cols-12 lg:pb-28">
        <div className="lg:col-span-7">
          <FadeUp>
            <p className="eyebrow" data-testid="sobre-eyebrow">Sobre</p>
          </FadeUp>
          <FadeUp delay={0.08}>
            <h1 className="mt-5 font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="sobre-title">
              Prazer, sou <em className="italic text-raiz-700">Sabrina Mota.</em>
            </h1>
          </FadeUp>
          <FadeUp delay={0.16} className="mt-8 max-w-xl space-y-5 text-base leading-relaxed text-ink/70">
            <p>
              Sou {PROFESSIONAL.fullName}, psicóloga e neuropsicóloga. Minha trajetória na prática clínica começou em 2021, ainda durante a graduação, por meio dos estágios em Psicologia.
            </p>
            <p>
              Em 2023 concluí minha formação em Psicologia e dei continuidade à prática clínica e ao aprofundamento profissional na área da Neuropsicologia.
            </p>
            <p>
              Atendo crianças, adolescentes e adultos, presencialmente e online, além de realizar avaliações e reavaliações neuropsicológicas.
            </p>
            <p>
              Acredito em uma Psicologia que compreenda a pessoa para além de sintomas ou diagnósticos. História, contexto, relações, dificuldades e potencialidades também fazem parte dessa compreensão.
            </p>
            <p className="font-serif text-lg italic text-raiz-800">É a partir desse olhar que construo meu trabalho.</p>
          </FadeUp>
          <FadeUp delay={0.24} className="mt-9 border-l-2 border-raiz-600/40 pl-5" data-testid="sobre-signature">
            <p className="font-serif text-xl text-raiz-900">{PROFESSIONAL.name}</p>
            <p className="text-sm text-ink/60">{PROFESSIONAL.titles}</p>
            <p className="text-sm text-ink/60">{PROFESSIONAL.crp}</p>
          </FadeUp>
        </div>
        <div className="relative lg:col-span-5">
          <div aria-hidden="true" className="absolute -bottom-6 -right-6 h-full w-full rounded-t-[999px] bg-sand" />
          <MaskReveal className="relative overflow-hidden rounded-t-[999px]">
            <img
              src="/assets/sabrina-sobre.jpg"
              alt="Sabrina Mota, psicóloga e neuropsicóloga, de jaleco branco"
              data-testid="sobre-photo"
              className="aspect-[4/5] w-full object-cover"
              width="1200"
              height="1500"
            />
          </MaskReveal>
        </div>
      </div>
    </section>

    <section className="border-t border-raiz-900/10 bg-cream py-20 lg:py-28" data-testid="trajetoria-section">
      <div className="container-x">
        <FadeUp>
          <p className="eyebrow">Trajetória</p>
        </FadeUp>
        <Stagger className="mt-10 grid gap-6 md:grid-cols-3" gap={0.12}>
          {TRAJETORIA.map((t) => (
            <StaggerItem key={t.year}>
              <div className="h-full rounded-3xl border border-raiz-900/10 bg-paper p-8" data-testid={`trajetoria-${t.year}`}>
                <p className="font-serif text-4xl font-light text-raiz-600">{t.year}</p>
                <p className="mt-4 text-sm leading-relaxed text-ink/65">{t.text}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    <Raizes />

    <section className="border-t border-raiz-900/10 py-20 lg:py-24" data-testid="orienta-section">
      <div className="container-x">
        <FadeUp>
          <p className="eyebrow">O que orienta meu trabalho</p>
        </FadeUp>
        <FadeUp delay={0.08}>
          <h2 className="mt-5 max-w-2xl font-serif text-3xl font-light leading-[1.12] tracking-tight text-ink sm:text-4xl" data-testid="orienta-title">
            O que você encontrará aqui
          </h2>
        </FadeUp>
        <Stagger className="mt-10 flex flex-wrap gap-3" gap={0.08}>
          {["Escuta individualizada", "Atuação ética", "Conhecimento científico", "Respeito à singularidade", "Comunicação clara"].map((v) => (
            <StaggerItem key={v}>
              <span className="inline-block rounded-full border border-raiz-800/20 bg-cream px-6 py-3 font-serif text-lg italic text-raiz-800 transition-colors duration-300 hover:bg-raiz-800 hover:text-paper">
                {v}
              </span>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    <section className="py-20 lg:py-28" data-testid="valores-section">
      <div className="container-x">
        <FadeUp>
          <p className="eyebrow">Fundamentos</p>
        </FadeUp>
        <FadeUp delay={0.08}>
          <h2 className="mt-5 max-w-2xl font-serif text-3xl font-light leading-[1.12] tracking-tight text-ink sm:text-4xl lg:text-5xl" data-testid="valores-title">
            O que sustenta o nosso trabalho.
          </h2>
        </FadeUp>
        <Stagger className="mt-14 grid gap-10 lg:grid-cols-3" gap={0.12}>
          {PILARES.map((p) => (
            <StaggerItem key={p.n}>
              <div data-testid={`pilar-${p.title.toLowerCase()}`} className="border-t-2 border-raiz-800 pt-6">
                <p className="font-serif text-sm text-raiz-500">{p.n}</p>
                <h3 className="mt-2 font-serif text-2xl font-normal text-ink">{p.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-ink/65">{p.text}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <FadeUp delay={0.15} className="mt-16">
          <p className="text-xs uppercase tracking-[0.28em] text-raiz-600">Valores</p>
          <div className="mt-6 flex flex-wrap gap-3" data-testid="valores-chips">
            {VALORES.map((v) => (
              <span key={v} className="rounded-full border border-raiz-800/20 px-5 py-2.5 font-serif text-base italic text-raiz-800 transition-colors duration-300 hover:bg-raiz-800 hover:text-paper">
                {v}
              </span>
            ))}
          </div>
        </FadeUp>
      </div>
    </section>

    <CtaBand />
  </>
);

export default Sobre;
